import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import type { TelstraMobileReport } from '../services/telstraService';

export interface TelstraBlitzValidationModalProps {
  open: boolean;
  /** Customer name from the currently-selected/target customer (NOT the file). */
  targetCustomerName: string;
  /** Parsed report from the uploaded/downloaded blitz file. */
  parsedReport: TelstraMobileReport | null;
  /** Optional warnings from the parser. Rendered as a stacked alert list. */
  warnings?: string[];
  /** True while the upload is in flight; disables the confirm button. */
  saving?: boolean;
  /**
   * Called when the user confirms. Receives the report (with statementMonth
   * possibly overridden by the user) so the caller can pass it to the upload
   * endpoint.
   */
  onConfirm: (reportToSave: TelstraMobileReport) => void;
  onCancel: () => void;
}

/**
 * Customer-name + statement-period confirmation dialog used before saving a
 * Telstra blitz report against a customer profile. Shared by:
 *
 *   1. Telstra.tsx — manual upload from the customer Telstra tab
 *   2. BlitzReportApprovalAction — approving a blitz that came from the
 *      recurring tasks / Zendesk pipeline
 *
 * The component is purely presentational. The caller owns the upload call
 * and any post-save side effects (e.g. recording the approval).
 */
export function TelstraBlitzValidationModal({
  open,
  targetCustomerName,
  parsedReport,
  warnings,
  saving,
  onConfirm,
  onCancel,
}: TelstraBlitzValidationModalProps) {
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [overrideStatementMonth, setOverrideStatementMonth] = useState('');

  // Reset transient state every time the modal is reopened.
  useEffect(() => {
    if (open) {
      setCustomerConfirmed(false);
      setOverrideStatementMonth('');
    }
  }, [open]);

  if (!open || !parsedReport) return null;

  const handleConfirm = () => {
    const reportToSave = { ...parsedReport };
    if (overrideStatementMonth) {
      reportToSave.statementMonth = overrideStatementMonth;
    }
    onConfirm(reportToSave);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Confirm Customer</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Close"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <p className="text-sm text-slate-600 mb-4">
              Before approving this report, please verify that you are uploading it to the correct customer.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
              <div className="text-sm text-slate-500 mb-1">Customer name in uploaded file:</div>
              <div className="text-lg font-semibold text-slate-900">
                {parsedReport.customerName || 'Not found in file'}
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="text-sm text-orange-600 mb-1">You are uploading to:</div>
              <div className="text-lg font-semibold text-orange-900">
                {targetCustomerName || 'Unknown'}
              </div>
            </div>
          </div>

          {/* Statement Month Override */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="text-sm text-blue-700 mb-2 font-medium">Statement Period</div>
            <p className="text-xs text-blue-600 mb-3">
              Override the statement period if uploading a historical report. Leave empty to use the detected period.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-sm text-blue-600">Detected:</span>
              <span className="text-sm font-semibold text-blue-800">{parsedReport.statementMonth}</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <label htmlFor="override-month" className="text-sm text-blue-600">Override:</label>
              <input
                id="override-month"
                type="month"
                value={overrideStatementMonth}
                onChange={(e) => setOverrideStatementMonth(e.target.value)}
                className="px-3 py-1.5 border border-blue-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                placeholder="YYYY-MM"
              />
              {overrideStatementMonth && (
                <button
                  onClick={() => setOverrideStatementMonth('')}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Clear
                </button>
              )}
            </div>
            {overrideStatementMonth && (
              <div className="mt-2 text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1">
                Will save as: <strong>{overrideStatementMonth}</strong>
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 cursor-pointer mb-6 p-3 bg-slate-50 rounded-lg">
            <input
              type="checkbox"
              checked={customerConfirmed}
              onChange={(e) => setCustomerConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm text-slate-700">
              I confirm that the customer name in the uploaded file matches the customer I am submitting this report for.
            </span>
          </label>

          {!parsedReport.customerName && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Could not extract customer name from the uploaded file. Please manually verify this is the correct customer.
                </div>
              </div>
            </div>
          )}

          {warnings && warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4 space-y-1">
              {warnings.map((w, i) => (
                <div key={i} className="text-sm text-yellow-800">{w}</div>
              ))}
            </div>
          )}

        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-2xl">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="bg-green-600 text-white hover:bg-green-700"
            disabled={!customerConfirmed || !!saving}
            onClick={handleConfirm}
          >
            {saving ? 'Saving...' : 'Confirm & Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
